import React from 'react';
import { BriefcaseIcon, CarIcon, CheckIcon, ChefHatIcon, CircleIcon, GraduationCapIcon, HeartPulseIcon, HomeIcon, MegaphoneIcon, MoreHorizontalIcon, ShoppingCartIcon, SparklesIcon, TargetIcon, UploadCloudIcon, UtensilsIcon, VideoIcon } from 'lucide-react';
import { FormQuestion } from '../../types';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Select } from '../ui/Select';
import { Checkbox } from '../ui/Checkbox';
interface QuestionFieldProps {
  question: FormQuestion;
  value: string | string[] | undefined;
  onChange: (value: string | string[]) => void;
  error?: string;
  autoFocus?: boolean;
}

/** Icono sugerido según el texto de la opción. Puramente visual. */
const ICON_RULES: [RegExp, React.ComponentType<{
  className?: string;
}>][] = [[/inmobili/i, HomeIcon], [/cocina|reforma/i, ChefHatIcon], [/automo|coche|motor/i, CarIcon], [/restaura|hosteler/i, UtensilsIcon], [/ecommerce|tienda/i, ShoppingCartIcon], [/servicio/i, BriefcaseIcon], [/salud|bienestar|clínic/i, HeartPulseIcon], [/formaci|academ/i, GraduationCapIcon], [/public|campaña|anuncio/i, MegaphoneIcon], [/v[ií]deo|contenido/i, VideoIcon], [/automatiz|inteligencia|ia\b/i, SparklesIcon], [/cliente|venta|objetivo/i, TargetIcon], [/otro/i, MoreHorizontalIcon]];
function iconFor(option: string) {
  const match = ICON_RULES.find(([re]) => re.test(option));
  return match ? match[1] : CircleIcon;
}
export function QuestionField({
  question,
  value,
  onChange,
  error,
  autoFocus
}: QuestionFieldProps) {
  const describedBy = error ? `${question.id}-error` : undefined;
  function renderOptions(multiple: boolean) {
    const selected = Array.isArray(value) ? value : value ? [String(value)] : [];
    const descriptions = question.optionDescriptions ?? [];
    // Con descripciones o textos largos, una sola columna se lee mejor.
    const single = descriptions.length > 0 || question.options.some(option => option.length > 42);
    return <div role={multiple ? 'group' : 'radiogroup'} aria-labelledby={`${question.id}-label`} className={`grid gap-3 ${single ? '' : 'sm:grid-cols-2'}`} data-id="element-657" data-mp="1bhb08uybi9ks">
        {question.options.map((option, optionIndex) => {
        const active = selected.includes(option);
        const Icon = iconFor(option);
        return <button key={option} type="button" role={multiple ? 'checkbox' : 'radio'} aria-checked={active} onClick={() => {
          if (multiple) {
            onChange(active ? selected.filter(s => s !== option) : [...selected, option]);
          } else {
            onChange(option);
          }
        }} className={`group flex min-h-[60px] w-full items-start gap-3 rounded-xl border px-4 py-4 text-left text-[15px] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${active ? 'border-primary bg-primary/5 shadow-[0_6px_20px_-12px_rgba(61,139,255,0.9)]' : 'border-border bg-background hover:border-primary/40 hover:bg-surface'}`} data-id="element-658" data-mp="1gge44c2ka9r6" data-mpi={optionIndex}>
              <span className={`flex h-8 w-8 flex-none items-center justify-center rounded-lg transition-colors ${active ? 'bg-primary text-primary-foreground' : 'bg-surface text-muted-foreground group-hover:text-primary'}`} aria-hidden="true" data-id="element-659" data-mp="4zeddkvv42z9">
                {active ? <CheckIcon className="h-4 w-4" data-id="element-660" data-mp="27p79gf623m6u" /> : <Icon className="h-4 w-4" data-id="element-661" data-mp="27m2z101zzu7q" />}
              </span>
              <span className="min-w-0 flex-1" data-id="element-662" data-mp="2bqas19fhnwbr">
                <span className="block font-medium leading-snug text-foreground" data-id="element-663" data-mp="2zf2ava76v2bv">
                  {option}
                </span>
                {descriptions[optionIndex] && <span className="mt-1 block text-sm leading-relaxed text-muted-foreground" data-id="element-664" data-mp="34ddmnqh1nmwq">
                    {descriptions[optionIndex]}
                  </span>}
              </span>
            </button>;
      })}
      </div>;
  }
  let control: React.ReactNode;
  switch (question.type) {
    case 'texto_largo':
      control = <Textarea id={question.id} rows={5} autoFocus={autoFocus} placeholder={question.placeholder} value={typeof value === 'string' ? value : ''} onChange={e => onChange(e.target.value)} aria-describedby={describedBy} data-id="element-665" data-mp="1f1l9dgw1ezoq" />;
      break;
    case 'seleccion_unica':
      control = renderOptions(false);
      break;
    case 'seleccion_multiple':
      control = renderOptions(true);
      break;
    case 'si_no':
      control = <div role="radiogroup" className="grid gap-3 sm:grid-cols-2" data-id="element-666" data-mp="1lgtt38sx6lvb">
          {['Sí', 'No'].map(option => {
          const active = value === option;
          return <button key={option} type="button" role="radio" aria-checked={active} onClick={() => onChange(option)} className={`flex min-h-[60px] items-center justify-center rounded-xl border text-[15px] font-medium transition-all ${active ? 'border-primary bg-primary/5 text-foreground' : 'border-border bg-background text-foreground hover:border-primary/40'}`} data-id="element-667" data-mp="2i61caa62giw2">
                {option}
              </button>;
        })}
        </div>;
      break;
    case 'desplegable':
      control = <Select id={question.id} placeholder="Selecciona una opción" options={question.options.map(o => ({
        value: o,
        label: o
      }))} value={typeof value === 'string' ? value : ''} onChange={e => onChange(e.target.value)} aria-describedby={describedBy} data-id="element-668" data-mp="sqbf3cqnr034" />;
      break;
    case 'casilla':
      control = <Checkbox id={question.id} label={question.placeholder || 'Acepto'} checked={value === 'Sí'} onChange={e => onChange(e.target.checked ? 'Sí' : '')} data-id="element-669" data-mp="3demw16lmyvp0" />;
      break;
    case 'archivo':
    case 'imagen':
      control = <label htmlFor={question.id} className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-surface px-6 py-10 text-center transition-colors hover:border-primary/50" data-id="element-670" data-mp="3n83mq1qh7uog">
          <UploadCloudIcon className="h-6 w-6 text-muted-foreground" aria-hidden="true" data-id="element-671" data-mp="50eagihj756c" />
          <span className="text-sm font-medium text-foreground" data-id="element-672" data-mp="22qe42hxn0ho3">
            {typeof value === 'string' && value ? value : question.type === 'imagen' ? 'Sube una imagen' : 'Adjunta un archivo'}
          </span>
          <span className="text-xs text-muted-foreground" data-id="element-673" data-mp="3a1lqs9a0i263">
            {question.placeholder || 'Haz clic para seleccionar'}
          </span>
          <input id={question.id} type="file" accept={question.type === 'imagen' ? 'image/*' : undefined} className="sr-only" onChange={e => onChange(e.target.files?.[0]?.name ?? '')} data-id="element-674" data-mp="355es91jy3sy6" />
        </label>;
      break;
    default:
      {
        const inputType = question.type === 'email' ? 'email' : question.type === 'telefono' ? 'tel' : question.type === 'numero' ? 'number' : question.type === 'url' ? 'url' : question.type === 'fecha' ? 'date' : question.type === 'hora' ? 'time' : 'text';
        control = <Input id={question.id} type={inputType} autoFocus={autoFocus} placeholder={question.placeholder} value={typeof value === 'string' ? value : ''} onChange={e => onChange(e.target.value)} aria-describedby={describedBy} data-id="element-675" data-mp="6nr8kpwkcxf4" />;
      }
  }
  return <div className="space-y-6" data-id="element-676" data-mp="anryb6p44v1h">
      <div className="space-y-2" data-id="element-677" data-mp="aouqdhy693iz">
        <h3 id={`${question.id}-label`} className="text-[22px] font-semibold leading-snug tracking-tight text-navy sm:text-[26px]" data-id="element-678" data-mp="alfc2hcbnyim">
          {question.label}
          {question.required && <span className="ml-1 text-primary" aria-hidden="true" data-id="element-679" data-mp="z625nnb472pj">
              *
            </span>}
        </h3>
        {question.description && <p className="text-sm text-muted-foreground" data-id="element-680" data-mp="p9flz1uiyhp3">{question.description}</p>}
      </div>
      {control}
      {error && <p id={`${question.id}-error`} role="alert" className="text-sm font-medium text-red-600" data-id="element-681" data-mp="bhiy59zw2tyi">
          {error}
        </p>}
    </div>;
}